import { useState, useCallback, useEffect } from "react";
import apiClient from "../services/apiClient";
import Cookies from "js-cookie";
import { getErrorMessage } from "../utils/getErrorMessage";
import { queryClient } from "../services/queryClient";

// Cart merge helper - handles moving local items to the database
const mergeLocalCartToApi = async () => {
  const LOCAL_CART_KEY = "cartItems";
  try {
    const localCart = JSON.parse(localStorage.getItem(LOCAL_CART_KEY)) || [];
    if (localCart.length === 0) return;

    await Promise.all(
      localCart.map((item) =>
        apiClient.post("/api/cart", {
          product_id: item.productId,
          quantity: item.quantity,
          variations: item.variations || [],
        })
      )
    );

    localStorage.removeItem(LOCAL_CART_KEY);
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new Event("cart-updated"));
  } catch (err) {
    // Silently fail - don't block login if cart merge fails
  }
};

export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * LOGIN: Sets cookies, saves user to localStorage, and merges cart.
   */
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post("/api/auth/login", {
        email,
        password,
      });

      if (response.data.type === "success" && response.data.message?.token) {
        const token = response.data.message.token;
        const user = response.data.message.user;
        
        Cookies.set("authToken", token, { secure: true, sameSite: "Strict" });
        localStorage.setItem("user", JSON.stringify(user));

        await mergeLocalCartToApi();

        setLoading(false);
        return { success: true, token, user };
      }

      const errorMsg = response.data.message || "Invalid email or password";
      setError(errorMsg);
      setLoading(false);
      return { success: false, error: errorMsg };
    } catch (err) {
      const errorMsg = getErrorMessage(err, "Invalid email or password");
      setError(errorMsg);
      setLoading(false);
      return { success: false, error: errorMsg };
    }
  };

  /**
   * REGISTER: Creates a new user account.
   */
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post("/api/auth/create-account", {
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        password: userData.password,
        password_confirmation: userData.password_confirmation,
      });

      if (response.data.type === "success") {
        setLoading(false);
        return { success: true };
      }

      const errorMsg = response.data.message || "Registration failed";
      setError(errorMsg);
      setLoading(false);
      return { success: false, error: errorMsg };
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Registration failed";
      setError(errorMsg);
      setLoading(false);
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await apiClient.post("/api/auth/logout");
    } catch (err) {
      console.error(err);
    } finally {
      Cookies.remove("authToken");
      localStorage.removeItem("user");
      sessionStorage.clear();
      queryClient.clear();
      setError(null);
      setLoading(false);
      return { success: true };
    }
  };

  // Logout on browser/tab close
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isAuthenticated()) {
        logout();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const getUser = useCallback(() => {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  }, []);

  const isAuthenticated = () => {
    return !!Cookies.get("authToken");
  };

  const clearError = () => setError(null);

  return {
    login,
    register,
    logout,
    isAuthenticated,
    getUser,
    loading,
    error,
    clearError,
  };
};